import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Typography, useTheme, Button } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useGetAgencyConfiguration, useUpdateAgencyConfiguration } from 'apis/agency/configuration/hook';
import InfoIcon from 'assets/icons/InfoIcon';
import Input from 'components/Common/Input';
import PasswordInput from 'components/Common/PasswordInput';
import Spinner from 'components/Common/Spinner';
import { useForm } from 'react-hook-form';
import { twilioSchema } from 'utils/schema/validator/AgencyConfigureValidation';

export default function Twilio() {
  const theme = useTheme();
  const { data: configuration, isLoading } = useGetAgencyConfiguration();
  const { mutateAsync: updateConfiguration } = useUpdateAgencyConfiguration();

  const { handleSubmit, control, reset } = useForm({
    resolver: zodResolver(twilioSchema),
    values: { configuration: configuration }
  });

  const onSubmit = (data: any) => {
    updateConfiguration(data);
  };

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <Box>
      <Typography variant='h6'>Twilio</Typography>
      <Typography color='secondary' mt={1}>
        It helps streamline call routing, messaging, and real-time communication, enhancing efficiency and
        responsiveness.
      </Typography>
      <Box component='form' onSubmit={handleSubmit(onSubmit)} mt={2}>
        <Grid
          container
          spacing={2}
          bgcolor={theme.palette.background.paper}
          padding={3}
          borderRadius='4px'
          border='1px solid #ECECEC'
        >
          <Box
            sx={{
              backgroundColor: '#F0F6FF',
              border: '1px solid #D0E3FF',
              borderRadius: 2,
              padding: 1,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1
            }}
          >
            <Box
              component='span'
              sx={{
                width: 24,
                height: 24,
                mt: '2px',
                color: '#1E66F5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <InfoIcon />
            </Box>
            <Box>
              <Typography fontWeight={600} mb={0.5} color='#1E66F5'>
                Account Usage
              </Typography>
              <Typography color='#1E66F5'>
                When configured, these credentials will be used to purchase new phone numbers for your businesses. If
                left empty, Kickcall's default Twilio account will be used for number purchases.
              </Typography>
            </Box>
          </Box>
          <Grid size={12}>
            <Typography my={0.5} fontSize={16}>
              SSID
            </Typography>
            <Input name='configuration.twilio_account_sid' control={control} size='small' fullWidth />
          </Grid>
          <Grid size={12}>
            <Typography my={0.5} fontSize={16}>
              Auth Token
            </Typography>
            <Input name='configuration.twilio_auth_token' control={control} size='small' fullWidth />
          </Grid>
          <Grid size={12}>
            <Typography my={0.5} fontSize={16}>
              Trunk SID
            </Typography>
            <Input name='configuration.twilio_trunk_sid' control={control} size='small' fullWidth />
          </Grid>
          <Grid size={12}>
            <Typography my={0.5} fontSize={16}>
              Termination SIP URI
            </Typography>
            <Input name='configuration.termination_sip_uri' control={control} size='small' fullWidth />
          </Grid>
          <Grid size={12}>
            <Typography my={0.5} fontSize={16}>
              Termination Username
            </Typography>
            <Input name='configuration.termination_username' control={control} size='small' fullWidth />
          </Grid>
          <Grid size={12}>
            <Typography my={0.5} fontSize={16}>
              Termination Password
            </Typography>
            <PasswordInput name='configuration.termination_password' control={control} size='small' fullWidth />
          </Grid>
        </Grid>
        <Box mt={3} display='flex' gap={1} justifyContent='end'>
          <Button color='secondary' variant='outlined' sx={{ border: '1px solid #ECECEC' }} onClick={() => reset()}>
            Cancel
          </Button>
          <Button type='submit' variant='contained' color='primary'>
            Configure
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
